import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import mergeRefs from '../utils/mergeRefs';
import * as styles from './InputButton.module.css';

export type InputButtonProps = {
  description?: string;
  error?: string;
  name: string;
  icon?: React.ReactNode;
  value?: string | number | React.ReactNode;
  defaultValue?: string | number;
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    defaultValue?: string | number;
  };
  onClick?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  onBlur?: () => void;
};

export const InputButton = React.forwardRef<HTMLButtonElement, InputButtonProps>(
  (
    {
      description,
      error,
      name,
      icon,
      value,
      defaultValue,
      buttonProps = {},
      onClick,
      onKeyDown,
      onBlur,
    },
    ref
  ) => {
    const [state, setState] = useState({
      focused: false,
      touched: false,
    });

    const inputRef = useRef<HTMLButtonElement | null>(null);
    const inputEl = useMemo(() => mergeRefs([inputRef, ref]), [ref]);

    useEffect(() => {
      setState((prev) => ({
        ...prev,
        touched:
          (inputRef && inputRef.current && !!inputRef.current.textContent) ||
          Boolean(value) ||
          Boolean(defaultValue),
      }));
    }, [defaultValue, value]);

    const handleFocus = useCallback(() => {
      setState((prev) => ({ ...prev, focused: true }));
    }, []);

    const handleBlur = useCallback(() => {
      onBlur?.();
      setState((prev) => ({ ...prev, focused: false }));
    }, [onBlur]);

    const handleClick = useCallback(() => {
      onClick?.();
    }, [onClick]);

    const handleOnKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        onKeyDown?.(event);
      },
      [onKeyDown]
    );

    const buttonAttributes = {
      ...buttonProps,
      ...(defaultValue !== undefined ? { defaultValue } : {}),
      ...(onKeyDown ? { onKeyDown: handleOnKeyDown } : {}),
    };

    return (
      <div className={styles.root}>
        <div className={styles.buttonWrapper}>
          <div
            className={cn({
              [styles.formButton]: true,
              [styles.focused]: state.focused,
              [styles.error]: Boolean(error),
            })}
            {...(onClick ? { onClick: handleClick } : {})}
          >
            <button
              {...buttonAttributes}
              ref={inputEl}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={styles.button}
              name={name}
              type="button"
            >
              {value}
            </button>
            {icon && <span className={styles.icon}>{icon}</span>}
          </div>
        </div>
        {error && <div className={styles.errorLabel}>{error}</div>}
        {description && <div className={styles.description}>{description}</div>}
      </div>
    );
  }
);
